import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CalendarClock, Sparkles, Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWeeklyPaycheck, type WeeklyPaycheckResult } from "@/hooks/useWeeklyPaycheck";
import WeeklyPaydayModal from "./WeeklyPaydayModal";
import {
  CAREER_SELECTION_ROUTE,
  describeLock,
} from "@/lib/payroll/careerEligibility";
import { toast } from "sonner";

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const fmtDate = (d: Date) =>
  d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export default function IncomeOverviewCard() {
  const {
    loading,
    eligible,
    lockReason,
    careerKey,
    annualSalary,
    breakdown,
    nextPayday,
    claimedThisWeek,
    claim,
    claiming,
  } = useWeeklyPaycheck();
  const [paycheck, setPaycheck] = useState<WeeklyPaycheckResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleClaim = async () => {
    if (!eligible) return; // hard guard — UI shouldn't reach this branch
    const result = await claim();
    if (result) {
      setPaycheck(result);
      setModalOpen(true);
      toast.success(`+${fmt(result.netPay)} deposited`);
    } else if (claimedThisWeek) {
      toast.message("Already paid this week — your next payday is on the way.");
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-5 animate-pulse h-32" />
    );
  }

  if (!eligible || !breakdown) {
    const lock = describeLock(lockReason);
    return (
      <div className="glass-card relative overflow-hidden p-5 space-y-4">
        <div className="pointer-events-none absolute inset-x-0 -top-24 h-48 bg-gradient-to-b from-primary/15 via-primary/0 to-transparent blur-2xl" />
        <div className="relative flex items-center gap-2">
          <Lock className="w-4 h-4 text-primary" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Career Income
          </span>
        </div>
        <div className="relative space-y-1.5">
          <p className="font-heading font-bold text-lg text-foreground">{lock.title}</p>
          <p className="text-xs leading-relaxed text-muted-foreground">{lock.body}</p>
        </div>
        <Button
          onClick={() => navigate(CAREER_SELECTION_ROUTE)}
          size="sm"
          className="relative w-full gap-1.5"
        >
          <Briefcase className="h-3.5 w-3.5" />
          {lock.cta}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  const careerLabel = careerKey
    ? careerKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Career";

  return (
    <>
      <div className="glass-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Career Income
              </span>
            </div>
            <p className="mt-1 font-heading font-bold text-lg text-foreground">{careerLabel}</p>
            <p className="text-xs text-muted-foreground">
              {fmt(annualSalary)}/yr · paid weekly
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Take-home</p>
            <p className="font-heading text-xl font-bold text-primary">
              {fmt(breakdown.netPay)}
            </p>
            <p className="text-[10px] text-muted-foreground">/ week</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Gross" value={fmt(breakdown.weeklyGrossPay)} />
          <Stat
            label="Taxes"
            value={`−${fmt(breakdown.taxesWithheld)}`}
            tone="negative"
          />
          <Stat
            label="Benefits"
            value={`−${fmt(breakdown.benefitsDeduction)}`}
            tone="negative"
          />
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-muted/30 px-3 py-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" />
            <span>Next payday · {fmtDate(nextPayday)}</span>
          </div>
          <Button
            size="sm"
            onClick={handleClaim}
            disabled={claiming || claimedThisWeek}
            className="gap-1.5"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {claimedThisWeek ? "Paid this week" : claiming ? "Processing…" : "Claim paycheck"}
          </Button>
        </div>

        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Budget from your <span className="font-semibold text-foreground">net</span> pay — that's
          what actually reaches the Sim Wallet after taxes and deductions.
        </p>
      </div>

      <WeeklyPaydayModal
        open={modalOpen}
        paycheck={paycheck}
        careerLabel={careerLabel}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "negative";
}) {
  return (
    <div className="rounded-lg border border-border/40 bg-background/40 px-2 py-1.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={`text-sm font-semibold ${
          tone === "negative" ? "text-destructive" : "text-foreground"
        }`}
      >
        {value}
      </p>
    </div>
  );
}